import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePlayer } from '../lib/player'
import { toTitleCase } from '../lib/format'
import { artGradient } from '../lib/songColour'
import { initials } from './ui'

/**
 * The now-playing bar. Sits directly above the tab bar and stays there while
 * you browse — the whole reason the audio element moved above the router.
 *
 * Shape is the Apple Music / Spotify one, and for the same reason both landed
 * on it: artwork to identify, title to confirm, one control to stop, and the
 * whole bar as a tap target to get back to the thing itself. Progress stays a
 * 2px hairline visually — a fat scrubber would dominate a bar this small —
 * but it drags, the same trick Spotify uses on its own thin bars: the visible
 * line is decoration, a full-height invisible strip underneath is the real
 * hit target, so a thumb has something to land on without the bar getting
 * taller. Dragging it must not also open /learn underneath the thumb, so the
 * hit strip stops that tap from reaching .mini-open.
 */
export default function MiniPlayer() {
  const { track, playing, pos, len, failed, toggle, close, seek } = usePlayer()
  const nav = useNavigate()
  const barEl = useRef(null)
  const [drag, setDrag] = useState(null)   // 0..1 while dragging, else null
  if (!track) return null

  const pct = drag !== null ? drag * 100 : (len ? Math.min(100, (pos / len) * 100) : 0)

  const fractionAt = (clientX) => {
    const r = barEl.current?.getBoundingClientRect()
    if (!r?.width) return 0
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width))
  }
  const down = (e) => {
    if (!len) return
    e.stopPropagation()   // don't also trigger .mini-open underneath
    e.currentTarget.setPointerCapture(e.pointerId)
    setDrag(fractionAt(e.clientX))
  }
  const move = (e) => { if (drag !== null) setDrag(fractionAt(e.clientX)) }
  const up = (e) => {
    if (drag === null) return
    const f = fractionAt(e.clientX)
    setDrag(null)
    seek(f)
  }

  return (
    <div className="miniplayer" role="region" aria-label="Now playing">
      <div ref={barEl} className="mini-progress-hit"
           role="slider" aria-label="Seek" tabIndex={len ? 0 : -1}
           aria-valuemin={0} aria-valuemax={Math.round(len) || 0}
           aria-valuenow={Math.round(drag !== null ? drag * len : pos) || 0}
           onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
           onKeyDown={(e) => {
             if (!len) return
             const step = e.shiftKey ? 30 : 5
             if (e.key === 'ArrowRight') { e.preventDefault(); seek(Math.min(1, (pos + step) / len)) }
             else if (e.key === 'ArrowLeft') { e.preventDefault(); seek(Math.max(0, (pos - step) / len)) }
           }} />
      <span className={`mini-progress${drag !== null ? ' dragging' : ''}`}
            style={{ width: `${pct}%` }} aria-hidden="true" />

      {/* The artwork is the writer, so it goes to the writer -- the same rule as
          the byline in the feed. It sits inside .mini-open, so it has to stop
          its own tap from also opening the song underneath it. */}
      {track.writerId ? (
        <button className="mini-art is-art mini-art-link"
                style={{ background: artGradient(track.id) }}
                aria-label={`${track.writer || 'This writer'} — see their songs`}
                onClick={(e) => { e.stopPropagation(); nav(`/writer/${track.writerId}`) }}>
          {initials(track.writer || 'Beseter')}
        </button>
      ) : null}

      <button className="mini-open" onClick={() => nav(`/songs/${track.id}/learn`)}>
        {!track.writerId && (
          <span className="mini-art is-art" style={{ background: artGradient(track.id) }}
                aria-hidden="true">{initials(track.writer || 'Beseter')}</span>
        )}
        <span className="mini-meta">
          <b>{toTitleCase(track.title) || 'Untitled'}</b>
          <small>{failed ? "Can't play this here" : (track.writer || 'A writer')}</small>
        </span>
      </button>

      <button className="mini-btn" onClick={toggle} disabled={failed}
              aria-label={playing ? 'Pause' : 'Play'}>
        {playing ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <rect x="7" y="5" width="3.4" height="14" rx="1.1" />
            <rect x="13.6" y="5" width="3.4" height="14" rx="1.1" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5.6v12.8c0 .7.8 1.2 1.4.8l9.6-6.4c.5-.4.5-1.2 0-1.6L9.4 4.8c-.6-.4-1.4 0-1.4.8Z" />
          </svg>
        )}
      </button>

      <button className="mini-btn mini-close" onClick={close} aria-label="Stop and close">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2.2" strokeLinecap="round">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  )
}
